<template>
  <div animation="fade" :only-fade="true" class="review-tabs">
    <!-- Findings for customer -->
    <template v-if="findingsForResponsibleCustomer.length > 0">
      <div class="divider is-divider-blue" v-translate>Waiting on customer</div>
      <collapse-component accordion is-fullwidth>
        <collapse-item
          v-for="result in findingsForResponsibleCustomer"
          ref="collapseItem"
          v-bind="{ itemId: result.id }"
          :key="result.id"
          :title="getCollapseItemTitle(result)">
          <!-- Finding is waiting for customer to respond -->
          <comment-finding-list-component
            :finding="result.finding">
          </comment-finding-list-component>
          <!-- Finding form -->
          <div class="field">
            <label class="label" v-translate>Resolve non-conformity:</label>
            <div class="control">
              <label class="label" v-once v-translate>Current value:
                {{ result.getDependencyValue() }}
              </label>
              <!-- TODO: should remove the "<= 1" when the backend fixes this error: More than one data values provided -->
              <form-component
                v-if="result.formDescriptor.fieldSets.length <= 1"
                :formDescriptor="result.formDescriptor"
                :formData="result.formData">
              </form-component>
              <p class="help is-danger"
                v-if="result.finding.hasPendingComment() && !result.finding.pendingComment.isValid"
                v-translate>
                A comment is required
              </p>
            </div>
          </div>
          <div class="field is-grouped">
            <div class="control">
              <button class="button is-success" @click="saveReviewResult(result)"
                v-translate>
                Save and mark as complete
              </button>
              <label class="label" v-translate>Comment:</label>
              <textarea
                v-if="result.finding.hasPendingComment()"
                class="textarea"
                :ref="`textarea${result.id}`"
                v-model="result.finding.pendingComment.text"
                cols="30"
                rows="5">
              </textarea>
            </div>
          </div>
        </collapse-item>
      </collapse-component>
    </template>
    <!-- Finding for reviewer -->
    <template v-if="findingsForReviewerExists">
      <div>
        <div class="divider is-divider-green" v-translate>Waiting on auditor</div>
        <collapse-component accordion is-fullwidth>
          <collapse-item
            v-for="result in findingsForReviewer"
            ref="collapseItem"
            v-bind="{ itemId: result.id }"
            :key="result.id"
            :title="getCollapseItemTitle(result)">
            <!-- Finding resolved by customer and waiting for reviewer to accept or reject -->
            <div v-if="result.waitingOnReviewer(reviewerProfile)">
              <comment-finding-list-component
                :finding="result.finding">
              </comment-finding-list-component>
            </div>
          </collapse-item>
        </collapse-component>
      </div>
    </template>
  </div>
</template>
<script>
  import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
  import ProfileType from '@/classes/auth/ProfileType'
  import CollapseComponent from '@/components/layout/collapse/Collapse'
  import CollapseItem from '@/components/layout/collapse/Item'
  import CommentFindingListComponent from '@/components/layout/utility/CommentFindingListComponent'
  import FormComponent from '@/components/form/FormComponent'
  import { errorHandler } from '@/mixins/mixins'
  import { globalEventBus } from '@/utility/utility_functions'

  export default {
    name: 'case-review-findings-view',
    mixins: [errorHandler],
    props: {
      caseId: {
        required: true
      }
    },

    data () {
      return {
        kase: {}
      }
    },
    components: {
      CommentFindingListComponent,
      CollapseComponent,
      CollapseItem,
      FormComponent
    },
    computed: {
      ...mapState({
        selectedReviewResult: state => state.caseReviewModule.selectedReviewResult,
        authUser: state => state.authModule.authUser
      }),
      ...mapGetters([
        'getFindingsForResponsible',
        'getReviewResultFindings',
        'getCaseForId'
      ]),
      reviewerProfile () {
        return this.kase.reviewer
      },
      findingsForResponsibleCustomer () {
        return this.getFindingsForResponsible(ProfileType.CUSTOMER)
      },
      findingsForReviewer () {
        if (!this.reviewerProfile) {
          return []
        }

        return this.getFindingsForResponsible(this.reviewerProfile.profileType)
      },
      findingsForReviewerExists () {
        return this.findingsForReviewer.length > 0
      }
    },
    methods: {
      ...mapActions([
        'setCase',
        'saveComment',
        'updateReviewResultFormData',
        'updateReviewResultState',
        'getReviewResults'
      ]),
      ...mapMutations([
        'setStateReviewResultState',
        'addPendingComment'
      ]),
      getCollapseItemTitle (result) {
        let commentCount = result.finding ? result.finding.comments.length + 1 : 0
        return `${result.getLabel()} (${commentCount})`
      },

      focusCollapseItem (id) {
        this.$refs.collapseItem.forEach(collapseItem => {
          if (collapseItem.itemId === id) {
            collapseItem.toggle()
            this.$nextTick(() => {
              let textarea = this.$refs[`textarea${id}`]
              textarea && textarea[0] && textarea[0].focus()
            })
          } else if (collapseItem.isActive) {
            collapseItem.toggle()
          }
        })
      },
      saveReviewResult (reviewResult) {
        if (!this.isReviewResultValid(reviewResult)) {
          return
        }

        const comment = reviewResult.finding.pendingComment
        let postData = { finding: reviewResult.finding, comment: comment.text }

        this.saveComment(postData)
          .then(() => {
            return this.updateReviewResultFormData(reviewResult)
          }).then(() => {
            globalEventBus.$emit('reviewResultUpdated', reviewResult)
          }).catch((err) => {
            this.handleError(err)
          })
      },

      isReviewResultValid (reviewResult) {
        const comment = reviewResult.finding.pendingComment
        this.validateComment(comment)
        return this.validateReviewResult(reviewResult) && comment.isValid
      },
      validateReviewResult (reviewResult) {
        return reviewResult.formData.validate()
      },
      validateComment (comment) {
        comment.isValid = !comment.isEmpty()
        return comment
      }
    },
    created () {
      globalEventBus.$on('toggleCollapseItem', this.focusCollapseItem)
      this.setCase(this.caseId)
        .then(kase => {
          this.kase = kase
        })
    }
  }
</script>
